import React, { useState, useEffect } from "react";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Button } from "../ui/button";
import { useTheme } from "../../context/ThemeContext";
import { SITE_CONFIG } from "../../data/mock";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Features", href: "#features" },
    { label: "Pricing", href: "#pricing" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "py-2" : "py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <nav
          className={`flex items-center justify-between px-4 sm:px-6 py-3 rounded-full glass-nav border transition-theme ${
            isScrolled
              ? "shadow-lg border-[var(--border-light)]"
              : "border-transparent"
          }`}
        >
          {/* Logo */}
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
              <img
                src="logo.svg"
                className="w-8 h-8 rounded-xl"
                alt="Site Logo"
              />
            </div>
            <span className="font-bold text-xl text-[var(--text-primary)]">
              {SITE_CONFIG.name}
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="px-4 py-2 text-[var(--text-body)] opacity-85 hover:text-[var(--text-primary)] hover:bg-[var(--bg-section)] rounded-full transition-all duration-200 text-sm font-medium hover:opacity-100"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-[var(--bg-section)] transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-[var(--accent-primary)]" />
              ) : (
                <Moon className="w-5 h-5 text-[var(--text-secondary)]" />
              )}
            </button>

            {/* Login Button */}
            <a href={SITE_CONFIG.dashboardUrl} className="hidden sm:block">
              <Button
                variant="ghost"
                className="text-[var(--text-body)] opacity-85 hover:text-[var(--text-primary)] hover:bg-[var(--bg-section)] hover:opacity-100"
              >
                Login
              </Button>
            </a>

            {/* Get Started Button */}
            <a
              href={SITE_CONFIG.signupFormUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:block"
            >
              <Button className="btn-gradient text-white hover:opacity-90 rounded-full px-6">
                Get Started
              </Button>
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-full hover:bg-[var(--bg-section)] transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[var(--text-primary)]" />
              ) : (
                <Menu className="w-6 h-6 text-[var(--text-primary)]" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-2 p-4 rounded-2xl glass-nav border border-[var(--border-light)] shadow-lg">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 text-[var(--text-body)] opacity-85 hover:text-[var(--text-primary)] hover:bg-[var(--bg-section)] rounded-xl transition-all duration-200 font-medium hover:opacity-100"
                >
                  {link.label}
                </a>
              ))}
              <hr className="my-2 border-[var(--border-light)]" />
              <a
                href={SITE_CONFIG.dashboardUrl}
                className="px-4 py-3 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-section)] rounded-xl transition-all duration-200 font-medium"
              >
                Login
              </a>
              <a
                href={SITE_CONFIG.signupFormUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="w-full btn-gradient text-white hover:opacity-90 rounded-full mt-2">
                  Get Started
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
